import {useBookIfUniqueAndGoToConfirmation} from "@page-containers/booking/utils/useBookIfUniqueAndGoToConfirmation";
import {useSelectedDoctor} from "@page-containers/booking/utils/useSelectedDoctor";
import {MonolithLocationFields} from "@services/graphql/queries/location";
import {useTranslation} from "@i18n/client";
import React, {MouseEventHandler, PropsWithChildren} from "react";

import {I18nNamespace} from "@i18n";
import {supportLink} from "../../../_common/_constants";
import Button from "../../Button";
import Icon from "../../Icon";
import {AppointmentTile} from "../components/AppointmentTile";
import Heading from "../Heading";
import {useFamilyMembersForAuthedPatientQuery} from "./useFamilyMembersQuery";
import {useRouter} from "next/router";
import {buildBookingLink, parseBookingQuery} from "@page-containers/booking/utils/bookingQuery";
import {useEffectAsync} from "../../../_common/Carbon";

const Loading = () => (
  <div key="loading" className="w-full h-full flex items-center justify-center ">
    <Icon icon="logo" className="text-3xl leading-none text-gray-800 animate-spin" />
  </div>
);

const Avatar = ({children}: PropsWithChildren<unknown>) => (
  <span
    className="flex items-center justify-center bg-green-200 rounded-full h-12 w-12 body-lg"
    aria-hidden
  >
    {children}
  </span>
);

const PatientInfo = ({
  firstName,
  lastName,
  age,
}: {
  firstName: string;
  lastName: string;
  age: number;
}) => {
  const {t} = useTranslation(I18nNamespace.WEB_SCHEDULING);

  return (
    <span className="flex flex-col items-start">
      <span className="body-lg">{`${firstName} ${lastName}`}</span>
      <span className="body-sm text-gray-700">
        {t("{{count}} years old", {count: age, ns: "web-scheduling"})}
      </span>
    </span>
  );
};

const PatientButton = ({
  selected,
  onClick,
  children,
}: PropsWithChildren<{selected: boolean; onClick: MouseEventHandler}>) => (
  <button
    className="flex gap-3 items-center rounded-full border-none p-3 cursor-pointer bg-gray-100 hover:bg-gray-200 transition-colors"
    role="option"
    aria-selected={selected}
    onClick={onClick}
  >
    {children}
  </button>
);

const SupportRow = () => {
  const {t} = useTranslation(I18nNamespace.WEB_SCHEDULING);

  return (
    <div className="flex items-center justify-center gap-4">
      <span className="body-sm">{t("Need help?")}</span>
      <Button variant="ghost" size="xs" href={supportLink}>
        {t("Contact support", {ns: "web-scheduling"})}
      </Button>
    </div>
  );
};

const PatientSelection = ({
  location,
  selectedTime,
  apptReasonId,
  locationId,
  specialtyId,
  slotIds,
  dropoutVisitorId,
}: {
  location: MonolithLocationFields;
  selectedTime: number;
  apptReasonId: string;
  locationId: string;
  specialtyId: string;
  slotIds: string[];
  dropoutVisitorId?: string;
}) => {
  const {t} = useTranslation(I18nNamespace.WEB_SCHEDULING);
  const router = useRouter();

  const {familyMembers, loading} = useFamilyMembersForAuthedPatientQuery();

  const bookAppointment = useBookIfUniqueAndGoToConfirmation(undefined, dropoutVisitorId);
  const doctor = useSelectedDoctor();

  const onlyFamilyMember = familyMembers?.length === 1 && familyMembers[0];

  useEffectAsync(async () => {
    if (onlyFamilyMember) {
      const currentQuery = parseBookingQuery(router.query);
      await router.push(
        buildBookingLink({
          ...currentQuery,
          selectedPatientId: onlyFamilyMember.id,
        }),
      );
    }
  }, [onlyFamilyMember, router]);

  return (
    <div className="flex flex-col items-center">
      {loading || onlyFamilyMember ? (
        <Loading />
      ) : (
        <div className="flex flex-col gap-10 items-center">
          <div className="flex flex-col gap-6 items-center">
            <AppointmentTile
              firstName={doctor?.firstName || undefined}
              lastName={doctor?.lastName || undefined}
              credential={doctor?.credential || undefined}
              imageId={doctor?.coverImage?.id || location.coverImageId}
              locationTimezone={location.timezone}
              street={location.address.firstLine}
              city={location.address.city}
              time={selectedTime}
            />
            <Heading id="patient-select-label">
              {t("Who is this appointment for?", {ns: "web-scheduling"})}
            </Heading>
            <div
              role="listbox"
              aria-labelledby="patient-select-label"
              className="flex flex-col gap-2 max-w-[345px] w-full"
            >
              {familyMembers.map(patient => (
                <PatientButton
                  key={patient.id}
                  selected={false}
                  onClick={async () => {
                    bookAppointment(
                      {
                        patientId: patient.id,
                        locationId,
                        specialtyId,
                        apptReasonId,
                        slotIds,
                        virtual: false,
                        practiceId: location.practiceId,
                      },
                      {
                        locationSlug: location.slug,
                        selectedTime,
                        selectedPatientId: patient.id,
                      },
                    );
                  }}
                >
                  <Avatar>{patient.firstName[0]}</Avatar>
                  <PatientInfo
                    firstName={patient.firstName}
                    lastName={patient.lastName}
                    age={patient.age}
                  />
                </PatientButton>
              ))}
            </div>
          </div>
          <SupportRow />
        </div>
      )}
    </div>
  );
};

export default React.memo(PatientSelection);
